import { selectFilteredMessageLogMessageIds } from '@common-reducers/CampaignSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useState, useCallback } from 'react';
import { MessageLogCard } from './components/MessageLogCard';
import { MessageLogTable } from './components/MessageLogTable';
import { MessageLogFilters } from './components/MessageLogFilters';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LayoutGrid, List, Loader2 } from 'lucide-react';
import { MessageStatus } from '@common/models/message-log';
import { DBMessageLogThunks, messageLogSelectPaginationStatus } from '@common-reducers/DBServiceThunks';
import { Button } from '@/components/ui/button';

interface MessageLogsTabProps {
    supportedUserId: string | null;
}

export function MessageLogsTab({ supportedUserId }: MessageLogsTabProps) {
    const dispatch = useAppDispatch();
    const [selectedMedia, setSelectedMedia] = useState<any | null>(null);
    const [viewMode, setViewMode] = useState<'timeline' | 'list'>('list');
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    
    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<MessageStatus | 'all'>('all');
    const [attemptTypeFilter, setAttemptTypeFilter] = useState<'initial' | 'client-retry' | 'scheduler-retry' | 'all'>('all');
    const [whatsappStatusFilter, setWhatsappStatusFilter] = useState<string | 'all'>('all');

    // Get pagination status
    const { total, skip, limit } = useAppSelector(state => messageLogSelectPaginationStatus(state.DBMessageLogReducer));

    // Whether the message-logs fetch has completed. This flips to true once the
    // find resolves regardless of how many logs came back, so users with zero
    // logs no longer get stuck on the loading spinner forever.
    const itemsLoaded = useAppSelector(state => state.DBMessageLogReducer.itemsLoaded);

    // Use selector to get filtered messageIds
    const filteredMessageIds = useAppSelector(
        state => supportedUserId 
            ? selectFilteredMessageLogMessageIds(
                state, 
                supportedUserId, 
                searchQuery, 
                statusFilter, 
                attemptTypeFilter, 
                whatsappStatusFilter
            )
            : []
    );

    const handleClearFilters = () => {
        setSearchQuery('');
        setStatusFilter('all');
        setAttemptTypeFilter('all');
        setWhatsappStatusFilter('all');
    };

    const handleMediaClick = (message: any) => {
        setSelectedMedia(message);
    };

    // Get all messageIds for this user to know how many we have loaded
    const loadedMessageIdsCount = useAppSelector(
        state => supportedUserId 
            ? Object.values(state.DBMessageLogReducer.entities)
                .filter(log => log?.owner === supportedUserId).length
            : 0
    );

    const handleLoadMore = useCallback(async () => {
        if (!supportedUserId || isLoadingMore) return;
        
        setIsLoadingMore(true);
        try {
            await dispatch(DBMessageLogThunks.find({
                userId: supportedUserId,
                $sort: { _id: -1 },
                $skip: loadedMessageIdsCount,
                $limit: 500
            })).unwrap();
        } finally {
            setIsLoadingMore(false);
        }
    }, [dispatch, supportedUserId, isLoadingMore, loadedMessageIdsCount]);

    if (!supportedUserId) {
        return (
            <div className="flex items-center justify-center py-12 text-slate-500">
                No user selected
            </div>
        );
    }

    const hasMore = loadedMessageIdsCount < total;

    return (
        <div className="flex flex-col h-full gap-4">
            {/* Filters */}
            <MessageLogFilters
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                attemptTypeFilter={attemptTypeFilter}
                whatsappStatusFilter={whatsappStatusFilter}
                onSearchChange={setSearchQuery}
                onStatusFilterChange={setStatusFilter}
                onAttemptTypeFilterChange={setAttemptTypeFilter}
                onWhatsappStatusFilterChange={setWhatsappStatusFilter}
                onClearFilters={handleClearFilters}
            />

            {/* View Toggle */}
            <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                    Showing {total} logs in {filteredMessageIds.length} message{filteredMessageIds.length !== 1 ? 's' : ''}
                </div>
                <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'timeline' | 'list')}>
                    <TabsList>
                        <TabsTrigger value="timeline">
                            <LayoutGrid className="h-4 w-4 mr-2" />
                            Timeline
                        </TabsTrigger>
                        <TabsTrigger value="list">
                            <List className="h-4 w-4 mr-2" />
                            List
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
                {!itemsLoaded ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mb-4"></div>
                            <div className="text-slate-500">Loading message logs...</div>
                        </div>
                    </div>
                ) : (
                    <>
                        {viewMode === 'timeline' ? (
                            <div className="space-y-4">
                                {filteredMessageIds.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        No message logs found matching your filters
                                    </div>
                                ) : (
                                    filteredMessageIds.map((messageId) => (
                                        <MessageLogCard
                                            key={messageId}
                                            messageId={messageId}
                                            onMediaClick={handleMediaClick}
                                        />
                                    ))
                                )}
                            </div>
                        ) : (
                            <MessageLogTable
                                messageIds={filteredMessageIds}
                                onMediaClick={handleMediaClick}
                            />
                        )}

                        {hasMore && (
                            <div className="flex justify-center py-8">
                                <Button 
                                    variant="outline" 
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                    className="min-w-[150px]"
                                >
                                    {isLoadingMore ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        `Load More (${total - loadedMessageIdsCount} logs remaining)`
                                    )}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
